// ════════════════════════════════════════════════════════════════════
// TERROR — T11 · COIN SKINS
// ────────────────────────────────────────────────────────────────────
// caP'n, the roadmap, verbatim:
//   "Coin skins — cosmetic flip faces (mythic: J'ynxi's grin on both
//    sides — it still flips fair, she just wants you nervous)."
//
// D12 built the coin. This is the wardrobe for it — and by the
// roadmap's own note, T11 is "a data seam now, not an engine job."
// That is exactly what this file is: a TABLE plus one renderer. Adding
// a skin is a row. It is never a case in a switch, never a new prop
// threaded down through the Shatterfield, and never a second animation.
//
// ── THE ONE LAW THAT IS NOT COSMETIC ─────────────────────────────────
//
//        A SKIN NEVER TOUCHES THE OUTCOME.
//
// The flip is deterministic from `match.createdAt` parity, decided
// before this file is ever read, and every skin renders the SAME
// decision. J'ynxi's grin on both faces is a lie told to your eyes and
// to nobody else — the result line still names the true side, the true
// side still takes the first turn, and there is no dial anywhere in
// here that could bend a duel. If a future skin ever wants to change
// odds, that is a CARD, not a cosmetic.
//
// ── THE SHAPE, AND WHY IT IS THREE KNOBS AND NOT AN ENUM ─────────────
// A face is { glyph, bg, border, glow, cap, capColor }. `glyph` is a
// STRING the renderer switches on — not a component, not an import — so
// a skin that wants an existing glyph on a new metal is one row with no
// code at all, and a skin that wants a NEW glyph is one row plus one
// case. THE EXCEPTION LAW: the four glyphs below are the ones that
// exist today, not the set that can ever exist.
//
// ── STORAGE ──────────────────────────────────────────────────────────
//   profiles/{uid}/coinSkin/
//     equipped : string (skin id; the catalog is THIS FILE — code is
//                the catalog truth, same as backdrops and pulses)
//     unlocked : { [id]: { source, ts } }   ← APPEND-ONLY
//
// Byte-for-byte the shape of roomBackdrops and drawPulse, deliberately.
// A NEW top-level profile field needs no rules deploy (IE_FIREBASE_LAW
// §1 — the hard $extra lock lives only on collection/$cardId).
//
// ── ◆ WHICH CURRENCY A SKIN COSTS ───────────────────────────────────
// A `currency` field per row, not a rule keyed off rarity — because the
// day a legendary wants to be a ◆ item, or a mythic wants to be earned
// rather than sold, that must be a row edit and not an argument with a
// switch statement. (THE EXCEPTION LAW.)
//
// The default it happens to encode today IS canon, though, and it comes
// from THE MYTHICAL LAW rather than from taste: a Mythical is what you
// cannot get a second one of by doing the same thing again, and that is
// the same line as "costs Divine Crests." You never have to ask whether
// something is Mythical separately from whether it costs ◆ — they are
// one question. So the mythic row is "DC" and every other row is "CC".
//
// ⚠ Names and captions are V'ael's placeholder prose. Every one is a
// string in a row; caP'n's canon veto costs a keystroke.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase";
import { DivineCrest } from "./CoinGlyph";

// ── THE LADDER ────────────────────────────────────────────────────────
// The same three rungs the Fountain, the Story Set and the Titles use.
// There is no fourth.
export const SKIN_RARITIES = ["fable", "legendary", "mythic"];
export const SKIN_RARITY_META = {
  fable:     { label: "FABLE",     color: "#8fb8e8", order: 0 },
  legendary: { label: "LEGENDARY", color: "#e8b84b", order: 1 },
  mythic:    { label: "MYTHIC",    color: "#f43f5e", order: 2 },
};

// ── THE DEFAULT ───────────────────────────────────────────────────────
// D12's coin, exactly as it shipped, expressed as a row. It is free,
// everyone owns it, and it is what an unknown or missing skin id falls
// back to — so a profile carrying a skin from a future build renders
// the shipped coin instead of a hole.
export const DEFAULT_SKIN = "solrend";

// ── THE CATALOG ───────────────────────────────────────────────────────
//   id       — permanent. Never rename; provenance breaks.
//   name     — the shelf label.
//   rarity   — one of SKIN_RARITIES.
//   cost     — ◕ at the shelf. 0 = not for sale (default, or earned).
//   blurb    — one line for the shelf.
//   heads/tails — the two faces. See THE SHAPE above.
//   resultText  — what the banner shouts. Per FACE, because a skin whose
//                 faces are identical still has to announce the truth.
//   resultColor — the banner's colour, per face.
// ⚠ TEST PRICING, 2026-08-19. caP'n: "honestly just make every
// single item in the shop 1 xD". Every non-zero price on this shelf
// is 1 so the whole economy can be walked end to end in an evening.
// A zero stays a zero -- zero means "already yours", not "free".
// THIS IS ONE COMMIT. `git revert` it and the real numbers return.
export const COIN_SKINS = {
  solrend: {
    id: "solrend", name: "The Solrend Coin", rarity: "fable", cost: 0, currency: "CC",
    blurb: "The struck Crest against Pierre's snake. What everyone starts with.",
    heads: {
      glyph: "crest", cap: "DIVINE CREST", capColor: "#713f12",
      bg: "radial-gradient(circle at 35% 28%, #fff3c4 0%, #e8b84b 32%, #a16207 76%, #713f12 100%)",
      border: "3px solid #fde68a",
      glow: "0 0 36px #e8b84b66, inset 0 0 20px #78350f99, inset 0 2px 2px #fffbe888",
      resultText: "THE DIVINE CREST!", resultColor: "#e8b84b",
    },
    tails: {
      glyph: "pips", cap: "SNAKE EYES", capColor: "#86efac",
      bg: "radial-gradient(circle at 35% 28%, #14532d 0%, #052e16 55%, #011007 100%)",
      border: "3px solid #16a34a",
      glow: "0 0 36px #16a34a55, inset 0 0 20px #000d, inset 0 2px 2px #4ade8033",
      resultText: "SNAKE EYES!", resultColor: "#4ade80",
    },
  },

  obsidian: {
    id: "obsidian", name: "Gilded Obsidian", rarity: "fable", cost: 1, currency: "CC",
    blurb: "Pierre's house metal. Heavier than it looks, quieter than it sounds.",
    heads: {
      glyph: "crest", cap: "THE HOUSE", capColor: "#e8d9a8",
      bg: "radial-gradient(circle at 35% 28%, #3b3427 0%, #17130c 55%, #060403 100%)",
      border: "3px solid #c9a84c",
      glow: "0 0 36px #c9a84c44, inset 0 0 22px #000e, inset 0 2px 2px #f0d06033",
      resultText: "THE HOUSE STANDS!", resultColor: "#f0d060",
    },
    tails: {
      glyph: "pips", cap: "THE WELL", capColor: "#9fb8d4",
      bg: "radial-gradient(circle at 35% 28%, #1f2937 0%, #0b1220 60%, #030509 100%)",
      border: "3px solid #64748b",
      glow: "0 0 36px #64748b44, inset 0 0 22px #000e, inset 0 2px 2px #cbd5e133",
      resultText: "THE WELL ANSWERS!", resultColor: "#94a3b8",
    },
  },

  necropolis: {
    id: "necropolis", name: "The Necropolis Penny", rarity: "legendary", cost: 1, currency: "CC",
    blurb: "Pulled off the pile. Whichever way it lands, something down there noticed.",
    heads: {
      glyph: "eye", cap: "THE PILE REMEMBERS", capColor: "#e9d5ff",
      bg: "radial-gradient(circle at 35% 28%, #6d28d9 0%, #3b0764 58%, #12021f 100%)",
      border: "3px solid #a78bfa",
      glow: "0 0 36px #8b5cf666, inset 0 0 20px #1e0533cc, inset 0 2px 2px #ddd6fe44",
      resultText: "THE PILE REMEMBERS!", resultColor: "#c084fc",
    },
    tails: {
      glyph: "eye", cap: "THE PILE FORGETS", capColor: "#bfdbfe",
      bg: "radial-gradient(circle at 35% 28%, #1e3a8a 0%, #0b1e4a 58%, #01060f 100%)",
      border: "3px solid #60a5fa",
      glow: "0 0 36px #3b82f666, inset 0 0 20px #020a1acc, inset 0 2px 2px #dbeafe44",
      resultText: "THE PILE FORGETS!", resultColor: "#93c5fd",
    },
  },

  aeternus: {
    id: "aeternus", name: "The Aeternus Ingot", rarity: "legendary", cost: 1, currency: "CC",
    blurb: "Not struck. Poured, once, and never cooled all the way through.",
    heads: {
      glyph: "crest", cap: "STILL POURING", capColor: "#fee2b3",
      bg: "radial-gradient(circle at 35% 28%, #fff7ed 0%, #fb923c 30%, #9a3412 74%, #431407 100%)",
      border: "3px solid #fdba74",
      glow: "0 0 40px #fb923c66, inset 0 0 22px #7c2d12aa, inset 0 2px 2px #fff7ed88",
      resultText: "STILL POURING!", resultColor: "#fdba74",
    },
    tails: {
      // Not the snake — that is Pierre's mark and this is not Pierre's
      // coin. An ingot poured once bears the same struck crest on both
      // faces; only the metal remembers which side cooled first.
      glyph: "crest", cap: "ALREADY COOL", capColor: "#cbd5e1",
      bg: "radial-gradient(circle at 35% 28%, #e2e8f0 0%, #94a3b8 34%, #334155 76%, #0f172a 100%)",
      border: "3px solid #e2e8f0",
      glow: "0 0 40px #94a3b866, inset 0 0 22px #0f172aaa, inset 0 2px 2px #f8fafc88",
      resultText: "ALREADY COOL!", resultColor: "#e2e8f0",
    },
  },

  // ── THE ONE caP'n NAMED ─────────────────────────────────────────────
  // Both faces are her. The flip is still honest — the banner names the
  // true side and the true side moves first — but you will not know
  // which way it went until the words arrive, and that is the entire
  // product. It is the only skin in the table whose two faces are the
  // same picture, and the `fair` note below is a comment on purpose:
  // the fairness is enforced by the flip living somewhere else, not by
  // a flag in here that somebody could one day set to false.
  jynxis_grin: {
    // ◆ ON THE SHELF (caP'n, 2026-08-18: "put it in the shop for now").
    // ◆12 by THE CREST UNIT is three focused hours — the same rung the
    // Foil Vault charges for a holo_jynxi single, which is the right
    // neighbour for her. "For now" is doing real work in his sentence:
    // if she should be EARNED instead, delete the cost and grant her
    // from wherever she ought to come from. The shelf handles cost 0 as
    // "owned" already, so that change is one field.
    id: "jynxis_grin", name: "J'ynxi's Grin", rarity: "mythic", cost: 1, currency: "DC",
    blurb: "Her face on both sides. It still flips fair. She just wants you nervous.",
    heads: {
      glyph: "grin", cap: "HEHE", capColor: "#bbf7d0",
      bg: "radial-gradient(circle at 35% 28%, #4ade80 0%, #15803d 42%, #052e16 100%)",
      border: "3px solid #86efac",
      glow: "0 0 40px #22c55e77, inset 0 0 22px #022c1699, inset 0 2px 2px #dcfce788",
      resultText: "SHE LIKES YOU TODAY!", resultColor: "#4ade80",
    },
    tails: {
      glyph: "grin", cap: "HEHE", capColor: "#bbf7d0",
      bg: "radial-gradient(circle at 35% 28%, #4ade80 0%, #15803d 42%, #052e16 100%)",
      border: "3px solid #86efac",
      glow: "0 0 40px #22c55e77, inset 0 0 22px #022c1699, inset 0 2px 2px #dcfce788",
      resultText: "SHE LIKES THEM TODAY!", resultColor: "#4ade80",
    },
  },
  // ⭐ THE PUPIL'S PRIZE, 2026-08-22. caP'n: *"the middle pupil will
  // give you a Mythical SHV Backdrop and Coin and Lux/Grim aura."*
  // Earned at ASS-50, the deepest door in Minerium One, and available
  // by no other route — `cost: 0` because it is not for sale and is
  // not going to be. It is listed with `currency: "DC"` anyway so that
  // if it is ever put on a shelf it lands in the right lane without
  // anyone having to remember the mythic-is-Crests law.
  //
  // HEADS is the griddle, hot. TAILS is the plate gone cold — the same
  // object after he lost it, which is the only joke this coin makes
  // and it makes it once.
  shv_griddle: {
    id: "shv_griddle", name: "The Colonel's Griddle", rarity: "mythic", cost: 0, currency: "DC",
    blurb: "Struck from the Crimson Jewel itself. He never spent one, which is most of what there is to say about him.",
    heads: {
      glyph: "griddle", cap: "THE GRIDDLE", capColor: "#3a2210",
      bg: "radial-gradient(circle at 35% 28%, #fdf6e3 0%, #e6c98a 26%, #c17a2e 62%, #5a3312 100%)",
      border: "3px solid #fdf6e3",
      glow: "0 0 40px #c17a2e88, inset 0 0 22px #3a221099, inset 0 2px 2px #fffbe8aa",
      resultText: "THE GRIDDLE IS HOT!", resultColor: "#e6c98a",
    },
    tails: {
      glyph: "crest", cap: "GONE COLD", capColor: "#c8b8a0",
      bg: "radial-gradient(circle at 35% 28%, #2a2018 0%, #140e0a 58%, #050302 100%)",
      border: "3px solid #7a5a34",
      glow: "0 0 34px #7a4419aa, inset 0 0 24px #000e, inset 0 2px 2px #c8963c33",
      resultText: "COLD, AND STILL HIS.", resultColor: "#c8963c",
    },
  },

};

export const SKIN_LIST = Object.values(COIN_SKINS);

// Unknown / missing / future id → the shipped coin. Never null, never a
// hole where the coin should be.
export const skinOf = (id) => COIN_SKINS[id] || COIN_SKINS[DEFAULT_SKIN];

// Which lane a skin is bought in. Defaults to Chocolate Coins for a row
// that forgets to say — the pocket-money lane is the safe default,
// because the failure mode is "charged the wrong cheap currency" rather
// than "spent the Empire's reserve by accident".
export const currencyOf = (id) => (skinOf(id)?.currency === "DC" ? "DC" : "CC");

// The shelf's ordering: cheapest first inside rarest-last. A shelf that
// reads like a ladder sells the ladder.
export const shelfOrder = () =>
  [...SKIN_LIST].sort((a, b) => {
    const r = SKIN_RARITY_META[a.rarity].order - SKIN_RARITY_META[b.rarity].order;
    return r || a.cost - b.cost;
  });

// ── THE GLYPHS ────────────────────────────────────────────────────────
// One case per glyph string. Four today. Adding a fifth is a case here
// and a row above; nothing else in the game needs to know.
function CoinGlyphFor({ glyph, size = 54 }) {
  if (glyph === "crest") return <DivineCrest size={size} />;
  if (glyph === "pips") {
    return (
      <>
        <div style={{ display: "flex", gap: size * 0.3 }}>
          {[0, 1].map((k) => (
            <div key={k} style={{
              width: size * 0.31, height: size * 0.31, borderRadius: "50%",
              background: "radial-gradient(circle at 35% 30%, #fca5a5, #dc2626 60%, #7f1d1d)",
              boxShadow: "0 0 10px #dc2626aa",
            }} />
          ))}
        </div>
        <div style={{ fontSize: size * 0.37, marginTop: 1, filter: "drop-shadow(0 1px 2px #000)" }}>🐍</div>
      </>
    );
  }
  if (glyph === "griddle") {
    // ⭐ SIR H'OSH VROWN'S OWN, and the fifth glyph this file has ever
    // needed. Drawn rather than emoji'd for the same reason her mouth
    // is: a platform font update must never be able to change what a
    // player owns. The round plate, one sear across it, four shreds
    // along the sear, and his Jewel at the centre — the same mark
    // SourceSigil stamps on the cards he pays out, at coin scale.
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="33" fill="none" stroke="#fdf6e3" strokeWidth="5" />
        <g stroke="#fdf6e3" strokeWidth="6" strokeLinecap="round">
          <path d="M24 68 L38 58" />
          <path d="M62 42 L76 32" />
        </g>
        <g stroke="#c17a2e" strokeWidth="4" strokeLinecap="round">
          <path d="M31 75 L39 69" /><path d="M21 58 L29 52" />
          <path d="M71 48 L79 42" /><path d="M63 31 L71 25" />
        </g>
        <path d="M50 38 L61 50 L50 62 L39 50 Z" fill="#ff3c5a" stroke="#fdf6e3" strokeWidth="3" />
      </svg>
    );
  }
  if (glyph === "grin") {
    // Her mouth, drawn rather than emoji'd so it scales with the coin
    // and cannot be swapped out by a platform font update.
    return (
      <svg width={size} height={size * 0.72} viewBox="0 0 64 46" aria-hidden="true">
        <path d="M6 16 C 20 6, 44 6, 58 16 C 46 40, 18 40, 6 16 Z"
              fill="#04170c" stroke="#bbf7d0" strokeWidth="2.5" strokeLinejoin="round" />
        {[12, 22, 32, 42, 52].map((x, i) => (
          <path key={i} d={`M${x} ${13 + (i % 2)} L${x + 5} ${26 - (i % 2) * 3} L${x + 10} ${13 + (i % 2)} Z`}
                fill="#f0fdf4" />
        ))}
        <circle cx="20" cy="9" r="3" fill="#dcfce7" />
        <circle cx="44" cy="9" r="3" fill="#dcfce7" />
      </svg>
    );
  }
  if (glyph === "eye") {
    return (
      <svg width={size} height={size * 0.62} viewBox="0 0 64 40" aria-hidden="true">
        <ellipse cx="32" cy="20" rx="30" ry="17" fill="#0b0316" stroke="currentColor" strokeWidth="2" opacity="0.9" />
        <circle cx="32" cy="20" r="10" fill="currentColor" opacity="0.85" />
        <circle cx="32" cy="20" r="4" fill="#050208" />
        <circle cx="28" cy="16" r="2" fill="#fff" opacity="0.75" />
      </svg>
    );
  }
  // Unknown glyph: a struck blank. Loud enough to notice in playtest,
  // harmless enough to ship past.
  return <div style={{ fontSize: size * 0.8, opacity: 0.5 }}>◍</div>;
}

// ── THE RENDERER ──────────────────────────────────────────────────────
// The two faces of a coin, skin-driven. Shatterfield owns the spin, the
// perspective wrapper and the keyframes; this owns what the metal looks
// like. That split is why a new skin is a row: nothing here knows the
// duel exists.
//
//   <CoinFaces skinId={skinId} side="heads" />
//   <CoinFaces skinId={skinId} side="tails" />   ← pre-rotated 180°
//
// ── `flat` — AND WHY IT HAD TO EXIST ─────────────────────────────────
// The 3D coin needs `backface-visibility: hidden` and a 180° pre-rotation
// on the tails face; that is what makes a spinning coin show one side at
// a time. A flat SHELF preview needs neither, and the first cut tried to
// cancel the rotation with an equal rotation on a wrapper.
//
// That does not work, and the reason is worth writing down:
// backface-visibility is evaluated from the ELEMENT'S OWN transform, not
// from its accumulated position on screen. rotateY(180deg) turns this
// element's back toward the viewer, so it is hidden — no matter what any
// ancestor does. The shelf rendered heads only, both previews centred,
// and it looked deliberate. Caught in the live lap, not by inspection.
//
// So `flat` drops both properties instead of fighting them.
export function CoinFaces({ skinId, side = "heads", size = 132, flat = false }) {
  const skin = skinOf(skinId);
  const f = skin[side] || skin.heads;
  return (
    <div style={{
      position: "absolute", inset: 0, borderRadius: "50%",
      backfaceVisibility: flat ? "visible" : "hidden",
      transform: (!flat && side === "tails") ? "rotateY(180deg)" : undefined,
      background: f.bg, border: f.border, boxShadow: f.glow,
      color: f.capColor,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: 3,
    }}>
      <CoinGlyphFor glyph={f.glyph} size={size * 0.41} />
      <div style={{ fontSize: Math.max(6, size * 0.057), letterSpacing: 2.5, fontWeight: 900, color: f.capColor }}>
        {f.cap}
      </div>
    </div>
  );
}

// What the banner says once the coin lands. Takes the TRUE side, always.
export const bannerFor = (skinId, side) => {
  const f = skinOf(skinId)[side === "tails" ? "tails" : "heads"];
  return { text: f.resultText, color: f.resultColor };
};

// ── THE HOOK ──────────────────────────────────────────────────────────
// Live equipped skin for a uid. Falls back to the shipped coin at every
// step: no uid, no node, unknown id — all render the Solrend.
export function useCoinSkin(uid) {
  const [id, setId] = useState(DEFAULT_SKIN);
  useEffect(() => {
    if (!uid) { setId(DEFAULT_SKIN); return; }
    const r = ref(db, `profiles/${uid}/coinSkin/equipped`);
    return onValue(r, (snap) => setId(snap.val() || DEFAULT_SKIN),
      () => setId(DEFAULT_SKIN));
  }, [uid]);
  return id;
}

const coinSkins = {
  SKIN_RARITIES, SKIN_RARITY_META, DEFAULT_SKIN, COIN_SKINS, SKIN_LIST,
  skinOf, shelfOrder, CoinFaces, bannerFor, useCoinSkin,
};
export default coinSkins;
